/**
 * Readiness panel — Mohit's backfill checklist.
 * Consumes GET /api/pm/scorecard/readiness/ via useScorecardReadiness.
 *
 * Shown as a panel on ScorecardPage when the "Readiness" view toggle is
 * active. Clarity over polish per the reliability design spec — this is an
 * internal working checklist, not a stakeholder-facing surface.
 *
 * Contents:
 *  (a) Cohort summary — size, in-scope, excluded (no project), cutoff date
 *  (b) Per-field coverage list — present/total, coverage %, which metrics it feeds
 *  (c) Per-task table — missing vs. present fields for each in-scope task
 */
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, Box, Skeleton, Stack, Typography } from '@mui/material';
import type { ReactElement } from 'react';

import { useScorecardReadiness } from '@/hooks/useScorecardReadiness';
import type { ScorecardReadinessParams } from '@/types/scorecard';

interface ReadinessPanelProps {
  params: ScorecardReadinessParams;
}

function CohortStat({ label, value }: { label: string; value: string | number }): ReactElement {
  return (
    <Stack spacing={0.25} sx={{ minWidth: 96 }}>
      <Typography
        sx={{
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '0.625rem',
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          color: 'text.faintest',
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '1.375rem',
          fontWeight: 700,
          color: 'text.primary',
          lineHeight: 1,
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

function coverageColor(pct: number): string {
  if (pct >= 80) return '#6BFFAB';
  if (pct >= 40) return '#FFB000';
  return '#ef4444';
}

export function ReadinessPanel({ params }: ReadinessPanelProps): ReactElement {
  const { data, isLoading, error } = useScorecardReadiness(params);

  if (error) {
    return (
      <Alert severity="error" role="alert" sx={{ mb: 2.5 }}>
        Failed to load readiness data: {error.message}
      </Alert>
    );
  }

  if (isLoading && !data) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rectangular" height={88} sx={{ borderRadius: '8px' }} />
        <Skeleton variant="rectangular" height={220} sx={{ borderRadius: '8px' }} />
        <Skeleton variant="rectangular" height={220} sx={{ borderRadius: '8px' }} />
      </Stack>
    );
  }

  if (!data) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography sx={{ color: 'text.disabled' }}>No readiness data for this period.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* (a) Cohort summary */}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          background: 'background.paper',
          px: 2.25,
          py: 1.75,
          mb: 2.5,
        }}
        aria-label="Cohort summary"
      >
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.625rem',
            letterSpacing: '0.6px',
            textTransform: 'uppercase',
            color: 'text.disabled',
            mb: 1.25,
          }}
        >
          {data.cohort.cutoff ? `Cohort — since ${data.cohort.cutoff}` : 'Cohort — all tasks'}
        </Typography>
        <Stack direction="row" spacing={3} flexWrap="wrap">
          <CohortStat label="Total tasks" value={data.cohort.size} />
          <CohortStat label="In scope" value={data.cohort.in_scope} />
          <CohortStat label="Excluded (no project)" value={data.cohort.excluded_no_project} />
          <CohortStat label="Cutoff" value={data.cohort.cutoff ?? 'None'} />
        </Stack>
      </Box>

      {/* (b) Per-field coverage list */}
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h6" sx={{ color: 'text.primary', mb: 0.5 }}>
          Field coverage
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '0.8125rem', mb: 1.5 }}>
          Counted over tasks whose stage requires each field — Backlog owes nothing, and a
          checkbox only counts once it&apos;s actually ticked.
        </Typography>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '6px',
            overflow: 'hidden',
          }}
        >
          {data.fields.map((f, i) => (
            <Box
              key={f.field}
              sx={{
                display: 'grid',
                gridTemplateColumns: '160px 1fr 90px 1fr',
                alignItems: 'center',
                gap: 1.5,
                p: '10px 16px',
                borderBottom: i < data.fields.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
                background: i % 2 === 0 ? 'transparent' : 'background.elev2',
              }}
            >
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'text.primary' }}>
                {f.label}
              </Typography>
              <Box
                sx={{ height: 8, borderRadius: '4px', background: 'background.deeper', overflow: 'hidden' }}
                role="img"
                aria-label={`${f.label}: ${f.present} of ${f.total} present, ${f.coverage_pct}% coverage`}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${Math.min(f.coverage_pct, 100)}%`,
                    background: coverageColor(f.coverage_pct),
                    borderRadius: '4px',
                    transition: 'width 200ms ease',
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: coverageColor(f.coverage_pct),
                  textAlign: 'right',
                }}
              >
                {f.present}/{f.total} · {f.coverage_pct}%
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  color: 'text.faintest',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={`Feeds: ${f.feeds.join(', ')}`}
              >
                feeds: {f.feeds.join(', ')}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* (c) Per-task missing/present table */}
      <Box>
        <Typography variant="h6" sx={{ color: 'text.primary', mb: 1.5 }}>
          Tasks needing backfill ({data.tasks.length})
        </Typography>
        {data.tasks.length === 0 ? (
          <Typography sx={{ color: 'text.disabled', fontSize: '0.875rem' }}>
            No in-scope tasks with missing fields — the cohort is fully backfilled.
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: '6px' }}>
            <Box
              component="table"
              sx={{ borderCollapse: 'collapse', width: '100%', minWidth: 640 }}
              aria-label="Tasks needing backfill"
            >
              <Box component="thead">
                <Box component="tr">
                  {['Task', 'Project', 'Stage', 'Missing', 'Present', 'Notion'].map((h) => (
                    <Box
                      key={h}
                      component="th"
                      sx={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: '0.5625rem',
                        fontWeight: 700,
                        letterSpacing: '0.6px',
                        textTransform: 'uppercase',
                        color: 'text.disabled',
                        textAlign: 'left',
                        p: '10px 14px',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        background: 'background.deeper',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {data.tasks.map((task) => (
                  <Box key={task.notion_id} component="tr">
                    <Box
                      component="td"
                      sx={{
                        p: '9px 14px',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        fontSize: '0.8125rem',
                        color: 'text.primary',
                        maxWidth: 240,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.8125rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: 240,
                        }}
                        title={task.title}
                      >
                        {task.title}
                      </Typography>
                    </Box>
                    <Box
                      component="td"
                      sx={{
                        p: '9px 14px',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        fontSize: '0.75rem',
                        color: task.project ? 'text.secondary' : 'error.main',
                      }}
                    >
                      {task.project ?? 'No project'}
                    </Box>
                    <Box
                      component="td"
                      sx={{
                        p: '9px 14px',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        fontSize: '0.75rem',
                        color: 'text.secondary',
                      }}
                    >
                      {task.stage}
                    </Box>
                    <Box
                      component="td"
                      sx={{ p: '9px 14px', borderBottom: '1px solid', borderColor: 'divider' }}
                    >
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {task.missing.map((field) => (
                          <Box
                            key={field}
                            component="span"
                            sx={{
                              fontFamily: '"JetBrains Mono", monospace',
                              fontSize: '0.5625rem',
                              fontWeight: 600,
                              px: 0.75,
                              py: '2px',
                              borderRadius: '3px',
                              color: '#ef4444',
                              background: 'rgba(239,68,68,.12)',
                            }}
                          >
                            {field}
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                    <Box
                      component="td"
                      sx={{ p: '9px 14px', borderBottom: '1px solid', borderColor: 'divider' }}
                    >
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {task.present.map((field) => (
                          <Box
                            key={field}
                            component="span"
                            sx={{
                              fontFamily: '"JetBrains Mono", monospace',
                              fontSize: '0.5625rem',
                              fontWeight: 600,
                              px: 0.75,
                              py: '2px',
                              borderRadius: '3px',
                              color: '#6BFFAB',
                              background: 'rgba(107,255,171,.10)',
                            }}
                          >
                            {field}
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                    <Box
                      component="td"
                      sx={{ p: '9px 14px', borderBottom: '1px solid', borderColor: 'divider' }}
                    >
                      {task.notion_url ? (
                        <Box
                          component="a"
                          href={task.notion_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Open "${task.title}" in Notion`}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px',
                            minHeight: 44,
                            fontFamily: '"JetBrains Mono", monospace',
                            fontSize: '0.6875rem',
                            fontWeight: 600,
                            color: 'primary.main',
                            textDecoration: 'none',
                            whiteSpace: 'nowrap',
                            '&:hover': { textDecoration: 'underline' },
                            '&:focus-visible': {
                              outline: '2px solid',
                              outlineColor: 'primary.main',
                              outlineOffset: 2,
                            },
                          }}
                        >
                          Notion
                          <OpenInNewIcon sx={{ fontSize: '0.8125rem' }} aria-hidden="true" />
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize: '0.6875rem', color: 'text.disabled' }}>—</Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
